import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StarRatings from 'react-star-ratings';
import { getRequest } from '../../requestUtils';
import { analyticsTrack } from '../../analyticsUtils';
import RegistrationModal from '../RegistrationModal';
import { isMobile } from '../../screenUtils';
import thumbUp from '../../../assets/images/icons/thumbs-up.svg';
import currency from '../../../assets/images/icons/currency-us-gray.svg';
import mapPin from '../../../assets/images/icons/map-pin.svg';
import experience from '../../../assets/images/icons/experience.svg';
import './RecommendedConsultantCard.scss';

const yearsToText = (years) => {
  switch (years) {
    case 0:
      return ' (<1 yr)';
    case 1:
      return ' (1 yr)';
    default:
      return ` (${years} yrs)`;
  }
};

const truncate = (text, length) => (
  text.length > length ? `${text.slice(0, length)}...` : text
);

const truncateName = (text) => (
  isMobile() ? truncate(text, 15) : truncate(text, 25)
);

const matchingTags = (matchingFrom, matchingAgainst, complex) => (
  matchingFrom.filter((t) => matchingAgainst.includes(complex ? t.name : t))
);

const renderStarRatings = (rating, compact, company) => (
  <div className="rating-avg">
    <div className="g-row--always lowered-margin">
      {rating
        && (
        <span>
          <StarRatings
            rating={parseFloat(rating)}
            starRatedColor="#FED500"
            starHoverColor="#FED500"
            starEmptyColor="#FFFFFF"
            starDimension="16px"
            starSpacing="0px"
            numberOfStars={compact ? 1 : 5}
            name="rating_avg"
          />
          <span className="rating">
            {rating}
          </span>
        </span>
        )}
      {company && !compact
        && (
        <span className={`company ${rating ? '' : 'no-rating'}`}>
          {`${rating ? 'by ' : ''}${company}`}
        </span>
        )}
    </div>
  </div>
);

const renderInitialIcon = (initials) => (
  <div className="init-icon">
    <div className="icon-initials">
      {initials}
    </div>
  </div>
);

const renderRoleTag = (role, idx) => (
  <div className="role-tag" key={`${role.name}-${idx}`}>
    {`${role.name} ${role.experience === null ? '' : role.experience}`}
  </div>
);

const loadConsultantTags = (consultant, selectedTags) => {
  const maxTagsToShow = 4;
  let tags = [];

  tags = tags.concat(
    consultant.area_of_expertises.map((aoe) => aoe.name),
    consultant.therapeutic_areas.map((ta) => ta.name),
    consultant.therapies.map((therapy) => therapy.name),
    consultant.diseases.map((disease) => disease.name),
    consultant.development_phases,
    consultant.degrees,
    consultant.seniority_levels,
  ).sort(
    (a, b) => {
      if (selectedTags.includes(a) && !selectedTags.includes(b)) { return -1; }
      if (!selectedTags.includes(a) && selectedTags.includes(b)) { return 1; }
      return 0;
    },
  );

  return tags.slice(0, maxTagsToShow);
};

class RecommendedConsultantCard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      proposalRequested: false,
      showRegistrationModal: false,
      callback: () => {},
      requestCallbackSelected: false,
    };

    this.loadRequestedStatus = this.loadRequestedStatus.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.handleOpenRequestProposal = this.handleOpenRequestProposal.bind(this);
    this.handleOpenViewProfile = this.handleOpenViewProfile.bind(this);
    this.handleShowRegistrationModal = this.handleShowRegistrationModal.bind(this);
  }

  componentDidMount() {
    const { projectResourceId } = this.props;
    if (projectResourceId) {
      this.loadRequestedStatus();
    }
  }

  loadRequestedStatus() {
    const { projectResourceId, consultant, csrfToken } = this.props;

    getRequest(
      `/projects/${projectResourceId}/project_invitations`,
      { consultant_resource_id: consultant.resource_id, request_proposal: true },
      csrfToken,
    ).then((response) => {
      const found = response.data.includes(consultant.resource_id);
      this.setState({ proposalRequested: found });
    });
  }

  hideModal() {
    this.setState({ showRegistrationModal: false });
  }

  handleOpenRequestProposal() {
    const {
      consultant, location, filters, projectResourceId,
    } = this.props;
    let featureParam = '';
    if (location === 'Featured Consultants') {
      featureParam = '?featured=true';
      [
        'area_of_expertises', 'higher_level_roles', 'therapeutic_areas',
        'diseases', 'therapies',
      ].forEach((category) => {
        if (filters[`${category}.name`]) {
          featureParam += `&${category}=${filters[`${category}.name`]}`;
        }
      });
      ['development_phases', 'degrees', 'skills'].forEach((category) => {
        if (filters[category]) {
          featureParam += `&${category}=${filters[category]}`;
        }
      });
      if (filters.seniority_levels) {
        featureParam += `&levels=${filters.seniority_levels}`;
      }
    } else if (projectResourceId) {
      featureParam = `?project_id=${projectResourceId}`;
    }

    const url = `/users/${consultant.resource_id}/request_proposal${featureParam}`;

    window.open(url, '_blank');
  }

  handleOpenViewProfile() {
    const { consultant, projectResourceId } = this.props;
    const fromProject = projectResourceId ? `?project=${projectResourceId}` : '';
    const url = `/users/${consultant.resource_id}${fromProject}`;

    window.open(url, '_blank');
  }

  handleShowRegistrationModal(callback, requestCallbackSelected) {
    const { consultant, location, idx } = this.props;

    analyticsTrack('Opened registration modal', {
      location,
      ConsultantResourceID: consultant.resource_id,
      ClickPosition: idx,
    });

    this.setState({
      showRegistrationModal: true,
      callback,
      requestCallbackSelected,
    });
  }

  renderRequestProposal() {
    const {
      consultant, location, getEmailStatus, idx,
    } = this.props;
    const { proposalRequested } = this.state;

    if (proposalRequested) {
      return (
        <button
          className="btn btn-secondary requested-btn disabled"
        >
          <i className="fa fa-check proposal-check" />
          <span className="requested-text">Proposal Requested</span>
        </button>
      );
    }
    return (
      <button
        className="btn btn-primary request-btn"
        onClick={() => {
          analyticsTrack('Clicked request proposal on recommended consultant card', {
            location,
            ConsultantResourceID: consultant.resource_id,
            ClickPosition: idx,
          });
          if (getEmailStatus()) {
            this.handleOpenRequestProposal();
          } else {
            this.handleShowRegistrationModal(this.handleOpenRequestProposal, true);
          }
        }}
      >
        {`Connect with ${truncateName(consultant.name.split(' ')[0])}`}
      </button>
    );
  }

  renderTopRatedTag(className) {
    const { consultant } = this.props;

    if (consultant.rating_avg > 4) {
      return (
        <div className="top-rated-container">
          <img src={thumbUp} alt="thumbs-up" className={`thumbs-up ${className}`} />
          Top Rated
        </div>
      );
    }
    return null;
  }

  renderViewProfileLink(linkText, style) {
    const {
      consultant, location, getEmailStatus, idx,
    } = this.props;

    if (location !== 'Project Show Recommended Tab') {
      return (
        <button
          id={style}
          className={`btn btn-secondary-no-border ${style}`}
          onClick={() => {
            analyticsTrack('Clicked view profile on recommended consultant card', {
              location,
              ConsultantResourceID: consultant.resource_id,
              ClickPosition: idx,
            });
            if (getEmailStatus()) {
              this.handleOpenViewProfile();
            } else {
              this.handleShowRegistrationModal(this.handleOpenViewProfile, false);
            }
          }}
        >
          {linkText}
        </button>
      );
    }
    return (
      <button
        id={style}
        className={`btn btn-secondary-no-border ${style}`}
        onClick={this.handleOpenViewProfile}
      >
        {linkText}
      </button>
    );
  }

  renderHigherLevelRoles() {
    const { consultant, projectRole } = this.props;
    let currentReqRole = null;
    if (projectRole.length > 0) {
      currentReqRole = projectRole[0];
    }
    const reqRole = consultant.higher_level_roles.find((role) => role.name === currentReqRole);
    const fullDisplayCount = isMobile() ? 1 : 3;

    return (
      <div className="roles-container">
        {reqRole && renderRoleTag(reqRole, 'req')}
        {consultant.higher_level_roles
          .filter((role) => role !== reqRole)
          .slice(0, fullDisplayCount)
          .map((role, idx) => renderRoleTag(role, idx))}
        {consultant.higher_level_roles.length > fullDisplayCount
          && renderRoleTag({
            name: `+ ${consultant.higher_level_roles.length - fullDisplayCount} more`,
            experience: null,
          })}
      </div>
    );
  }

  renderProfileHeader() {
    const { consultant } = this.props;

    return (
      <div className="rec-profile-header">
        <div className="name-container">
          {renderInitialIcon(consultant.initials)}
          {this.renderViewProfileLink(truncateName(consultant.name), 'consultant-name')}
          { this.renderTopRatedTag() }
        </div>
        {this.renderHigherLevelRoles()}
      </div>
    );
  }

  renderTestimonial(className) {
    const { consultant } = this.props;
    const review = consultant.latest_review;

    if (!review || !(review.testimonial || review.avg_rating)) {
      return null;
    } if (className === 'mobile') {
      return (
        <div className="rec-testimonial mobile">
          { this.renderTopRatedTag() }
          {renderStarRatings(review.avg_rating, true)}
          {review.testimonial
            && (
            <a
              href={`/users/${consultant.resource_id}#reviews`}
              className="testimonial-link"
              target="_blank"
              rel="noreferrer"
            >
              Testimonials
            </a>
            )}
        </div>
      );
    }
    return (
      <div className={`rec-testimonial ${className}`}>
        <div className="section-header">Testimonial</div>
        {renderStarRatings(review.avg_rating, false, review.company)}
        {review.testimonial
        && (
        <div className="testimonial-review">
          {`"${review.testimonial}"`}
        </div>
        )}
      </div>
    );
  }

  renderAbout() {
    const { consultant, onProjectSharePage } = this.props;

    if (consultant.bio !== null && consultant.bio.length > 0) {
      return (
        <div className="rec-about">
          <div className="section-header">About</div>
          <div className={`bio-text ${onProjectSharePage && 'bio-text-share-page'}`}>{consultant.bio}</div>
        </div>
      );
    }

    return null;
  }

  renderTags() {
    const {
      consultant,
      location,
      filters,
      projectPriorityTags,
      projectNonPriorityTags,
    } = this.props;

    let tagsToRender = [];
    let selectedTags = [];
    Object.keys(filters).forEach((category) => {
      selectedTags = [...selectedTags, ...filters[category]];
    });

    if (location !== 'Project Show Recommended Tab') {
      tagsToRender = filters.length > 0
        ? filters
        : loadConsultantTags(consultant, selectedTags);
    } else {
      tagsToRender = projectPriorityTags.length > 0
        ? projectPriorityTags
        : matchingTags(projectNonPriorityTags, consultant.area_of_expertises, true);
    }

    const numExtraTags = consultant.num_total_tags - tagsToRender.length;

    return (
      <div className="rec-qualifications">
        { tagsToRender.map((tag) => (
          <div
            className={`rec-qualification-tag ${selectedTags.includes(tag) && 'highlighted'}`}
            key={tag}
          >
            { selectedTags.includes(tag) ? <i className="fa fa-check check-icon" /> : '' }
            {tag}
          </div>
        ))}
        {numExtraTags > 0
          && (
          <div className="rec-qualification-tag">
            {`+ ${numExtraTags} more`}
          </div>
          )}
      </div>
    );
  }

  renderAvailability() {
    const { consultant, location } = this.props;

    if (consultant.availability && location === 'Project Show Recommended Tab') {
      return (
        <div className="rec-availability">
          Open to New Projects
        </div>
      );
    }

    return null;
  }

  renderLogistics() {
    const { consultant, cloraEmployer } = this.props;

    const showCity = consultant.city_location && consultant.city_location.length > 0;
    const showHourlyRate = consultant.minimum_hourly_rate && consultant.maximum_hourly_rate;

    const show = showCity
      || consultant.years_of_experience
      || showHourlyRate;

    return (
      <div className={`rec-logistics ${show ? '' : 'hidden'}`}>
        {showCity
          && (
          <div>
            <img
              className="logistics-image"
              src={mapPin}
              alt="map-pin"
            />
            <span className="logistics-text">
              {consultant.city_location}
            </span>
          </div>
          )}
        {consultant.years_of_experience
          && (
          <div className="logistics-experience">
            <img
              className="logistics-image"
              src={experience}
              alt="experience"
            />
            <span className="logistics-text">
              {`${consultant.years_of_experience} years experience`}
            </span>
          </div>
          )}
        {showHourlyRate
          && (
          <div>
            <img
              className="logistics-image"
              src={currency}
              alt="currency"
            />
            <span className="logistics-text">
              {cloraEmployer
                ? `$${Math.round(consultant.minimum_emp_hourly_rate)} - $${Math.round(consultant.maximum_emp_hourly_rate)} / hr`
                : `$${Math.round(consultant.minimum_hourly_rate)} - $${Math.round(consultant.maximum_hourly_rate)} / hr`}
            </span>
          </div>
          )}
      </div>
    );
  }

  renderRecentCompanies() {
    const { consultant } = this.props;

    if (consultant.past_roles.length > 0) {
      return (
        <div className="rec-companies">
          <div className="section-header">Recent Companies</div>
          <ul className="companies-list">
            {consultant.past_roles.slice(0, 3).map((position, idx) => (
              <li className="company" key={`${position.company_name}-${idx}`}>
                <span className="company-name">
                  {position.company_name}
                </span>
                <span className="company-years">
                  {yearsToText(position.years_experience)}
                </span>
              </li>
            ))}
          </ul>
          {this.renderViewProfileLink('View more in profile', 'companies-profile-link')}
        </div>
      );
    }

    return null;
  }

  render() {
    const {
      consultant, location, csrfToken, inviterId,
      googleOmniauthUrl, msOmniauthUrl, signInUrl, projectResourceId,
    } = this.props;
    const { showRegistrationModal, requestCallbackSelected } = this.state;
    const topOfPage = location === 'Project Show Recommended Tab' ? '' : 'space-top';

    const searchShowRightPane = location === 'Project Show Recommended Tab'
      || consultant.past_roles.length > 0
      || (consultant.city_location && consultant.city_location.length > 0)
      || consultant.years_of_experience
      || (consultant.minimum_hourly_rate && consultant.maximum_hourly_rate);

    const searchLeftPaneStyling = searchShowRightPane ? 'border-enabled' : '';
    const searchRightPaneStyling = searchShowRightPane ? '' : 'hidden';

    const consultantParam = `?con_resource_id=${consultant.resource_id}`;
    const requestParam = requestCallbackSelected ? '&request=true' : '';
    const extraParams = {
      con_resource_id: consultant.resource_id,
      request: requestCallbackSelected,
    };

    return (
      <div className="rec-consultant-container">
        <div className="rec-consultant-content">
          <div className={`rec-consultant-left ${searchLeftPaneStyling}`}>
            {this.renderProfileHeader()}
            {this.renderLogistics()}
            {this.renderTestimonial('full-view')}
            {this.renderTestimonial('mobile')}
            {this.renderAbout()}
            {this.renderTags()}
            {this.renderRecentCompanies()}
          </div>
          <div className={`rec-consultant-right ${topOfPage} ${searchRightPaneStyling}`}>
            {this.renderAvailability()}
            {this.renderLogistics()}
            {this.renderRecentCompanies()}
          </div>
        </div>
        <div className="rec-consultant-actions">
          {this.renderViewProfileLink('View Profile', 'profile-link')}
          {this.renderRequestProposal()}
        </div>
        <RegistrationModal
          showModal={showRegistrationModal}
          csrfToken={csrfToken}
          hideModal={this.hideModal}
          googleOmniauthUrl={googleOmniauthUrl}
          msOmniauthUrl={msOmniauthUrl}
          signInUrl={`${signInUrl}${consultantParam}${requestParam}`}
          extraParams={extraParams}
          header={`Sign up to hire top-tier consultants like ${consultant.name}`}
          showTestimonials={false}
          location={location}
          inviterId={inviterId}
          projectResourceId={projectResourceId}
        />
      </div>
    );
  }
}

RecommendedConsultantCard.propTypes = {
  consultant: PropTypes.shape({}).isRequired,
  location: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  projectRole: PropTypes.arrayOf(PropTypes.string),
  projectPriorityTags: PropTypes.arrayOf(PropTypes.string),
  projectResourceId: PropTypes.string,
  projectNonPriorityTags: PropTypes.arrayOf(PropTypes.string),
  idx: PropTypes.number,
  getEmailStatus: PropTypes.func,
  filters: PropTypes.shape({}),
  cloraEmployer: PropTypes.bool.isRequired,
  googleOmniauthUrl: PropTypes.string.isRequired,
  msOmniauthUrl: PropTypes.string.isRequired,
  signInUrl: PropTypes.string.isRequired,
  onProjectSharePage: PropTypes.bool,
  inviterId: PropTypes.string,
};

RecommendedConsultantCard.defaultProps = {
  projectRole: [],
  projectPriorityTags: [],
  projectNonPriorityTags: [],
  projectResourceId: null,
  idx: 0,
  getEmailStatus: () => null,
  filters: {},
  onProjectSharePage: false,
  inviterId: null,
  location: '',
};

export default RecommendedConsultantCard;
